/**
 * CreateFolderModal Component
 * Modal for creating new folders
 */

import React, { useState } from 'react';
import { Modal, TextInput, ColorInput, Button, Group, Stack } from '@/components/ui';
import { IconFolder } from '@tabler/icons-react';
import { CreateFolderRequest } from '../types';

interface CreateFolderModalProps {
  opened: boolean;
  onClose: () => void;
  onCreate: (data: CreateFolderRequest) => Promise<void>;
  parentPath?: string;
  domain?: string;
  recordId?: string;
}

export const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  opened,
  onClose,
  onCreate,
  parentPath = '/',
  domain,
  recordId,
}) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3B82F6');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) return;

    setLoading(true);
    try {
      await onCreate({
        name: name.trim(),
        parentPath,
        domain,
        recordId,
        color,
      });
      setName('');
      setColor('#3B82F6');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Create New Folder"
      size="md"
    >
      <Stack gap="md">
        <TextInput
          label="Folder Name"
          placeholder="Enter folder name"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
          leftSection={<IconFolder size={16} />}
          data-autofocus
        />

        <ColorInput
          label="Folder Color"
          placeholder="Choose folder color"
          value={color}
          onChange={setColor}
        />

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={loading}
            disabled={!name.trim()}
          >
            Create Folder
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
